import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Chip,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { format } from 'date-fns';
import { 
  useGetNotificationCampaignsQuery,
  useDeleteNotificationCampaignMutation,
  useScheduleNotificationCampaignMutation,
  useCancelNotificationCampaignMutation
} from '../../services/connectApi';
import type { NotificationCampaign } from '../../models';
import ConfirmDialog from '../../components/ConfirmDialog';
import LoadingBackdrop from '../../components/LoadingBackdrop';
import { useSnackbar } from 'notistack';

const CampaignsList: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedCampaign, setSelectedCampaign] = useState<NotificationCampaign | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [scheduleConfirmOpen, setScheduleConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { data, isLoading, refetch } = useGetNotificationCampaignsQuery({ page: page + 1, page_size: pageSize });
  const campaigns = data?.results || [];
  React.useEffect(() => {
    if (data?.count !== undefined) setTotalCount(data.count);
  }, [data]);

  const [deleteNotificationCampaign, { isLoading: isDeleting }] = useDeleteNotificationCampaignMutation();
  const [scheduleCampaign, { isLoading: isScheduling }] = useScheduleNotificationCampaignMutation();
  const [cancelCampaign, { isLoading: isCanceling }] = useCancelNotificationCampaignMutation();

  const handleView = (id: string) => {
    navigate(`/connect/campaigns/${id}?mode=view`);
  };

  const handleEdit = (id: string) => {
    navigate(`/connect/campaigns/edit/${id}`);
  };

  const handleDuplicate = (campaign: NotificationCampaign) => {
    const duplicatedCampaign: Partial<NotificationCampaign> = {
      ...campaign,
      name: `${campaign.name} (Copy)`,
      status: 'draft',
      notification_count: 0,
      delivered_count: 0,
      read_count: 0,
      failed_count: 0,
    };
    delete duplicatedCampaign.id;
    delete duplicatedCampaign.scheduled_at;
    delete duplicatedCampaign.completed_at;
    delete duplicatedCampaign.created_at;
    delete duplicatedCampaign.updated_at;

    navigate('/connect/campaigns/new', { state: { campaign: duplicatedCampaign } });
  };

  const handleDeleteClick = (campaign: NotificationCampaign) => {
    setSelectedCampaign(campaign);
    setDeleteConfirmOpen(true);
  };

  const handleScheduleClick = (campaign: NotificationCampaign) => {
    setSelectedCampaign(campaign);
    setScheduleConfirmOpen(true);
  };

  const handleCancelClick = (campaign: NotificationCampaign) => {
    setSelectedCampaign(campaign);
    setCancelConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedCampaign) {
      try {
        await deleteNotificationCampaign(selectedCampaign.id).unwrap();
        enqueueSnackbar('Campaign deleted successfully', { variant: 'success' });
        refetch();
      } catch (error) {
        enqueueSnackbar('Failed to delete campaign', { variant: 'error' });
      }
    }
    setDeleteConfirmOpen(false);
  };

  const handleScheduleConfirm = async () => {
    if (selectedCampaign) {
      try {
        await scheduleCampaign({ id: selectedCampaign.id }).unwrap();
        enqueueSnackbar('Campaign scheduled successfully', { variant: 'success' });
        refetch();
      } catch (error) {
        enqueueSnackbar('Failed to schedule campaign', { variant: 'error' });
      }
    }
    setScheduleConfirmOpen(false);
  };

  const handleCancelConfirm = async () => {
    if (selectedCampaign) {
      try {
        await cancelCampaign(selectedCampaign.id).unwrap();
        enqueueSnackbar('Campaign canceled successfully', { variant: 'success' });
        refetch();
      } catch (error) {
        enqueueSnackbar('Failed to cancel campaign', { variant: 'error' });
      }
    }
    setCancelConfirmOpen(false);
  };

  const getStatusChip = (status: string) => {
    let color:
      | 'default'
      | 'primary'
      | 'secondary'
      | 'error'
      | 'info'
      | 'success'
      | 'warning';
    
    switch (status) {
      case 'draft':
        color = 'default';
        break;
      case 'scheduled':
        color = 'info';
        break;
      case 'in_progress':
        color = 'warning';
        break;
      case 'completed':
        color = 'success';
        break;
      case 'canceled':
        color = 'error';
        break;
      case 'failed':
        color = 'error';
        break;
      default:
        color = 'default';
    }

    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
        color={color}
        size="small"
      />
    );
  };

  const columns: GridColDef[] = [
    { 
      field: 'name', 
      headerName: 'Name', 
      flex: 1,
      minWidth: 180 
    },
    { 
      field: 'template_name', 
      headerName: 'Template', 
      flex: 1,
      minWidth: 180 
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams<NotificationCampaign, any>) => 
        getStatusChip(params.value),
    },
    {
      field: 'notification_count',
      headerName: 'Notifications',
      width: 120,
      renderCell: (params: GridRenderCellParams<NotificationCampaign, any>) => (
        <Chip
          icon={<NotificationsActiveIcon />}
          label={params.value}
          color="primary"
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: 'scheduled_at',
      headerName: 'Scheduled For',
      minWidth: 180,
      valueFormatter: (params: { value: string | null }) => {
        if (!params || !params.value) {
          return 'Not scheduled';
        }
        try {
          return format(new Date(params.value), 'MMM d, yyyy HH:mm');
        } catch (error) {
          console.error('Error formatting date:', error);
          return 'Invalid date';
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 260,
      renderCell: (params: GridRenderCellParams<NotificationCampaign, any>) => {
        const campaign = params.row as NotificationCampaign;
        return (
          <Box>
            <Tooltip title="View Details">
              <IconButton onClick={() => handleView(campaign.id)} size="small">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {campaign.status === 'draft' && (
              <>
                <Tooltip title="Edit">
                  <IconButton onClick={() => handleEdit(campaign.id)} size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Schedule">
                  <IconButton onClick={() => handleScheduleClick(campaign)} size="small" color="primary">
                    <PlayArrowIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton onClick={() => handleDeleteClick(campaign)} size="small">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            
            <Tooltip title="Duplicate">
              <IconButton onClick={() => handleDuplicate(campaign)} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {campaign.status === 'scheduled' && (
              <Tooltip title="Cancel">
                <IconButton onClick={() => handleCancelClick(campaign)} size="small" color="error">
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  if (isLoading) {
    return <Typography>Loading campaigns...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Notification Campaigns
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Schedule and manage notification campaigns
      </Typography>
      
      <Paper elevation={2} sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={campaigns}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          disableRowSelectionOnClick
          loading={isLoading}
          rowCount={totalCount}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'created_at', sort: 'desc' }],
            },
          }}
        />
      </Paper>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Campaign"
        content={`Are you sure you want to delete the campaign "${selectedCampaign?.name}"? This action cannot be undone.`}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <ConfirmDialog
        open={scheduleConfirmOpen}
        title="Schedule Campaign"
        content={`Are you sure you want to schedule the campaign "${selectedCampaign?.name}" for immediate processing? Notifications will be sent shortly after confirmation.`}
        confirmText="Schedule Now"
        confirmColor="primary"
        onCancel={() => setScheduleConfirmOpen(false)}
        onConfirm={handleScheduleConfirm}
      />

      <ConfirmDialog
        open={cancelConfirmOpen}
        title="Cancel Campaign"
        content={`Are you sure you want to cancel the campaign "${selectedCampaign?.name}"? No further notifications will be sent.`}
        confirmText="Cancel Campaign"
        confirmColor="error"
        onCancel={() => setCancelConfirmOpen(false)}
        onConfirm={handleCancelConfirm}
      />

      <LoadingBackdrop open={isScheduling || isCanceling || isDeleting} />
    </Box>
  );
};

export default CampaignsList;
